import { StrictMode, useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import "./index.css";
import { AuthProvider, AuthProviderProps } from "react-oidc-context";
import { getOidcConfig } from "./oidcConfig.ts";
import { RouterProvider } from "react-router";
import { router } from "./routes.tsx";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import * as Sentry from "@sentry/react";
import { captureConsoleIntegration } from "@sentry/react";
import { getConfig } from "./services/configService.ts";
import { useSentryStore } from "./stores/sentryStore.ts";

getConfig().then((config) => {
  Sentry.init({
    enabled: config.sentry.enabled,
    dsn: "https://fe280a94048de1c4c71eb3186d5cf5df@o4510381947486208.ingest.de.sentry.io/4510381964132432",
    // Do not send default PII (e.g. automatic IP address collection on events).
    sendDefaultPii: false,
    integrations: [
      captureConsoleIntegration({
        levels: ["error"],
      }),
      Sentry.feedbackIntegration({
        showBranding: false,
        colorScheme: "light",
        autoInject: false,
      }),
    ],
    environment: config.environment,
    // Explicitly null the IP address so Sentry's backend does not infer and
    // store it from the request. An explicit null disables server-side
    // IP auto-completion.
    beforeSend(event) {
      event.user = { ...event.user, ip_address: null };
      return event;
    },
  });
  useSentryStore.setState(() => {
    return { feedback: Sentry.feedbackIntegration() };
  });
});

const queryClient = new QueryClient();

function App() {
  const [oidcConfig, setOidcConfig] = useState<AuthProviderProps | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getOidcConfig()
      .then((config) => {
        setOidcConfig(config);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, []);

  if (loading) {
    return <div />;
  }

  if (error) {
    return <div>Error loading configuration: {error}</div>;
  }

  if (!oidcConfig) {
    return <div>Configuration not available</div>;
  }

  return (
    <AuthProvider {...oidcConfig}>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
        <ReactQueryDevtools
          initialIsOpen={false}
          buttonPosition={"bottom-left"}
        />
      </QueryClientProvider>
    </AuthProvider>
  );
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
